/* ============================================================================
   ACEPLORE · module-collection-banner.js — <lumina-collection-banner>
   Subtle vertical parallax on the banner background image as it scrolls through
   the viewport. Progressive enhancement only: with JS off (or reduced motion)
   the image is fully covered by CSS and nothing moves. Uses requestAnimationFrame
   coalescing + IntersectionObserver so we never compute while off-screen. Cleans
   up every listener/observer on disconnect.
   ========================================================================== */
(function () {
  'use strict';

  var Lumina = (window.Lumina = window.Lumina || {});
  var utils = Lumina.utils || {};
  var Base = Lumina.Element || window.HTMLElement;

  function reducedMotion() {
    if (utils.prefersReducedMotion) return utils.prefersReducedMotion();
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
      return false;
    }
  }

  // Maximum travel of the image (in px) across the full scroll pass.
  var MAX_SHIFT = 60;

  class LuminaCollectionBanner extends Base {
    connectedCallback() {
      this.image = this.querySelector('.lm-cbanner__media-img');
      this._raf = null;
      this._inView = false;
      this._enabled = false;

      // Nothing to parallax, or the merchant/user opted out of motion.
      if (!this.image || this.hasAttribute('data-parallax') === false) return;
      if (reducedMotion() || !('IntersectionObserver' in window)) return;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this._motionMql = null;

      // Pause work entirely while the banner is outside the viewport.
      this._io = new IntersectionObserver(this.onIntersect.bind(this), {
        rootMargin: '120px 0px 120px 0px'
      });
      this._io.observe(this);

      // Re-check the reduced-motion preference if it changes at runtime.
      try {
        this._motionMql = window.matchMedia('(prefers-reduced-motion: reduce)');
        this._onMotionChange = this.onMotionChange.bind(this);
        if (this._motionMql.addEventListener) {
          this._motionMql.addEventListener('change', this._onMotionChange);
        } else if (this._motionMql.addListener) {
          this._motionMql.addListener(this._onMotionChange);
        }
      } catch (e) { /* no-op */ }

      this._enabled = true;
    }

    disconnectedCallback() {
      this.teardown();
      if (this._io) { this._io.disconnect(); this._io = null; }
      if (this._motionMql) {
        if (this._motionMql.removeEventListener) {
          this._motionMql.removeEventListener('change', this._onMotionChange);
        } else if (this._motionMql.removeListener) {
          this._motionMql.removeListener(this._onMotionChange);
        }
        this._motionMql = null;
      }
      if (super.disconnectedCallback) super.disconnectedCallback();
    }

    onIntersect(entries) {
      for (var i = 0; i < entries.length; i++) {
        this._inView = entries[i].isIntersecting;
      }
      if (this._inView) {
        this.bind();
        this.schedule();
      } else {
        this.teardown();
      }
    }

    onMotionChange(e) {
      if (e.matches) {
        // User now prefers reduced motion: stop and reset to the static state.
        this.teardown();
        this._enabled = false;
        this.style.removeProperty('--lm-cbanner-shift');
      }
    }

    bind() {
      if (this._bound || !this._enabled) return;
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      this._bound = true;
    }

    teardown() {
      if (!this._bound) return;
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      this._bound = false;
      if (this._raf) { cancelAnimationFrame(this._raf); this._raf = null; }
    }

    onScroll() { this.schedule(); }

    onResize() { this.schedule(); }

    schedule() {
      if (this._raf || !this._inView || !this._enabled) return;
      this._raf = requestAnimationFrame(this.update.bind(this));
    }

    update() {
      this._raf = null;
      var rect = this.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      if (!vh) return;

      // progress: 0 when the banner's top hits the bottom of the viewport,
      // 1 when its bottom leaves the top — clamped to [0, 1].
      var total = rect.height + vh;
      var progress = (vh - rect.top) / total;
      progress = Math.max(0, Math.min(1, progress));

      // Map to a symmetric shift centered at the midpoint of the pass.
      var shift = (progress - 0.5) * MAX_SHIFT * -1;
      this.style.setProperty('--lm-cbanner-shift', shift.toFixed(2) + 'px');
    }
  }

  if ('customElements' in window && !customElements.get('lumina-collection-banner')) {
    customElements.define('lumina-collection-banner', LuminaCollectionBanner);
  }
})();
